import { mapToCamelCase } from '@consigli/utils';
import { z } from 'zod';
/**
 * How a recorded turn ended. A turn with no ending is `running` until an hour after it started,
 * then `never_finished`.
 */
export var BeamstarTurnOutcome;
(function (BeamstarTurnOutcome) {
    BeamstarTurnOutcome["DONE"] = "done";
    BeamstarTurnOutcome["ERROR"] = "error";
    BeamstarTurnOutcome["STOPPED"] = "stopped";
    BeamstarTurnOutcome["CONNECTION_LOST"] = "connection_lost";
    BeamstarTurnOutcome["NEVER_FINISHED"] = "never_finished";
    BeamstarTurnOutcome["RUNNING"] = "running";
})(BeamstarTurnOutcome || (BeamstarTurnOutcome = {}));
const timestampSchema = z.string().datetime({ offset: true });
export const beamstarPeriodTotalsSchema = z
    .object({
    sessions: z.number().int(),
    turns: z.number().int(),
    input_tokens: z.number().int(),
    cached_input_tokens: z.number().int(),
    output_tokens: z.number().int(),
    failed_turns: z.number().int(),
    stopped_turns: z.number().int(),
    lost_turns: z.number().int(),
    unfinished_turns: z.number().int(),
    failed_tool_calls: z.number().int(),
    flags: z.number().int(),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarDayTotalsSchema = z
    .object({
    day: z.string().date(),
    finished: z.number().int(),
    failed: z.number().int(),
    stopped: z.number().int(),
    lost: z.number().int(),
    unfinished: z.number().int(),
    input_tokens: z.number().int(),
    cached_input_tokens: z.number().int(),
    output_tokens: z.number().int(),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarFailingToolSchema = z
    .object({
    tool: z.string(),
    agent: z.string().nullable(),
    calls: z.number().int(),
    failed: z.number().int(),
    common_failure: z.string().nullable(),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarFlagSchema = z
    .object({
    turn_id: z.string(),
    conversation_id: z.string(),
    flagged_at: timestampSchema,
    comment: z.string(),
    building_id: z.string().nullable(),
    outcome: z.nativeEnum(BeamstarTurnOutcome),
    workflow_id: z.string().nullable(),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarSummarySchema = z
    .object({
    totals: beamstarPeriodTotalsSchema,
    per_day: z.array(beamstarDayTotalsSchema),
    failing_tools: z.array(beamstarFailingToolSchema),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarSessionSchema = z
    .object({
    conversation_id: z.string(),
    project_id: z.number().int().nullable(),
    building_id: z.string().nullable(),
    started_at: timestampSchema,
    last_turn_at: timestampSchema,
    turns: z.number().int(),
    failed_turns: z.number().int(),
    stopped_turns: z.number().int(),
    lost_turns: z.number().int(),
    unfinished_turns: z.number().int(),
    failed_tool_calls: z.number().int(),
    input_tokens: z.number().int(),
    cached_input_tokens: z.number().int(),
    output_tokens: z.number().int(),
    flags: z.number().int(),
    workflows: z.record(z.string(), z.number().int()),
})
    .strict()
    // Workflow ids key the workflows map, so they keep the spelling the building agent sent.
    .transform(({ workflows, ...session }) => ({ ...mapToCamelCase(session), workflows }));
export const getBeamstarSessionsResponseSchema = z.array(beamstarSessionSchema);
export const beamstarStepSchema = z
    .object({
    seq: z.number().int(),
    kind: z.string(),
    tool: z.string().nullable(),
    agent: z.string().nullable(),
    model: z.string().nullable(),
    arguments: z.string().nullable(),
    output: z.string().nullable(),
    ok: z.boolean().nullable(),
    output_tokens: z.number().int().nullable(),
    reasoning_tokens: z.number().int().nullable(),
    duration_ms: z.number().int().nullable(),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarTurnSchema = z
    .object({
    id: z.string(),
    seq: z.number().int(),
    message: z.string(),
    reply: z.string(),
    workflow_id: z.string().nullable(),
    started_at: timestampSchema,
    duration_ms: z.number().int().nullable(),
    outcome: z.nativeEnum(BeamstarTurnOutcome),
    error: z.string().nullable(),
    input_tokens: z.number().int(),
    cached_input_tokens: z.number().int(),
    output_tokens: z.number().int(),
    reasoning_tokens: z.number().int(),
    requests: z.number().int(),
    flag_comment: z.string().nullable(),
    flagged_at: timestampSchema.nullable(),
    steps: z.array(beamstarStepSchema),
})
    .strict()
    .transform(mapToCamelCase);
export const beamstarSessionDetailSchema = z
    .object({
    conversation_id: z.string(),
    project_id: z.number().int().nullable(),
    building_id: z.string().nullable(),
    created_at: timestampSchema,
    turns: z.array(beamstarTurnSchema),
})
    .strict()
    .transform(mapToCamelCase);
export const getBeamstarFlagsResponseSchema = z.array(beamstarFlagSchema);
